import React, { useState, useEffect } from "react";
import { adminCmsService, CmsSettings, UpdateCmsSettingsRequest } from "../../services/admin/cmsService";

const Cms: React.FC = () => {
  const [settings, setSettings] = useState<CmsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"social" | "homepage" | "config">("social");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminCmsService.getSettings();
      setSettings(data);
    } catch (err: any) {
      console.error("CMS Settings Error:", err);
      
      let errorMessage = "فشل تحميل الإعدادات";
      
      // Handle connection errors
      if (err.message?.includes("CONNECTION_REFUSED") || 
          err.message?.includes("Failed to fetch") ||
          err.status === 0) {
        errorMessage = "❌ لا يمكن الاتصال بالخادم. يرجى التأكد من أن الخادم يعمل على http://localhost:3000";
      }
      // Handle HTTP errors
      else if (err.status) {
        if (err.status === 401) {
          errorMessage = "❌ انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى";
        } else if (err.status >= 500) {
          errorMessage = `❌ خطأ في الخادم (${err.status}): ${err.message || "يرجى المحاولة مرة أخرى لاحقاً"}`;
        } else {
          errorMessage = `❌ ${err.message || `خطأ (${err.status})`}`;
        }
      }
      // Handle other errors
      else if (err.message) {
        errorMessage = `❌ ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (sectionData: Partial<UpdateCmsSettingsRequest>) => {
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Only send the fields that are being updated, not all settings
      const updateData: UpdateCmsSettingsRequest = {};
      
      // Copy only the fields from sectionData
      Object.keys(sectionData).forEach((key) => {
        const value = sectionData[key as keyof UpdateCmsSettingsRequest];
        // Convert empty strings to null
        updateData[key as keyof UpdateCmsSettingsRequest] = value === "" ? null : value;
      });

      const updated = await adminCmsService.updateSettings(updateData);
      setSettings(updated);
      setSuccess("✅ تم حفظ الإعدادات بنجاح");
      // Clear success message after 5 seconds instead of 3
      setTimeout(() => setSuccess(null), 5000);
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error("Save error:", err);
      
      let errorMessage = "فشل حفظ الإعدادات";
      
      // Handle connection errors
      if (err.message?.includes("CONNECTION_REFUSED") || 
          err.message?.includes("Failed to fetch") ||
          err.status === 0) {
        errorMessage = "❌ لا يمكن الاتصال بالخادم. يرجى التأكد من أن الخادم يعمل على http://localhost:3000";
      }
      // Handle validation errors
      else if (err.errors && Array.isArray(err.errors)) {
        const errorMessages = err.errors.map((e: any) => {
          if (e.message) return e.message;
          if (e.constraints) {
            return `${e.property}: ${Object.values(e.constraints).join(", ")}`;
          }
          return `${e.property}: خطأ في التحقق`;
        }).join("\n");
        errorMessage = `❌ ${errorMessages}`;
      }
      // Handle HTTP errors
      else if (err.status) {
        if (err.status === 400) {
          errorMessage = `❌ خطأ في البيانات المرسلة: ${err.message || "يرجى التحقق من صحة البيانات"}`;
        } else if (err.status === 401) {
          errorMessage = "❌ انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى";
        } else if (err.status === 403) {
          errorMessage = "❌ ليس لديك صلاحية للقيام بهذا الإجراء";
        } else if (err.status >= 500) {
          errorMessage = `❌ خطأ في الخادم (${err.status}): ${err.message || "يرجى المحاولة مرة أخرى لاحقاً"}`;
        } else {
          errorMessage = `❌ ${err.message || `خطأ (${err.status})`}`;
        }
      }
      // Handle other errors
      else if (err.message) {
        errorMessage = `❌ ${err.message}`;
      }
      
      setError(errorMessage);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const processImageFile = (file: File) => {
    // Validate file type - only PNG, JPG, JPEG
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setError(`❌ الملف ${file.name} ليس من نوع مدعوم. يرجى اختيار صورة PNG أو JPG أو JPEG`);
      return;
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`❌ الصورة ${file.name} كبيرة جداً (${(file.size / 1024 / 1024).toFixed(2)} MB). الحد الأقصى هو 10 MB`);
      return;
    }

    setError(null);
    setSuccess("جاري رفع الصورة...");

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      const imageData = base64.split(",")[1]; // Remove data:image/...;base64, prefix
      handleSave({ homePageImage: imageData });
    };
    reader.onerror = () => {
      setError("❌ فشل قراءة الملف. يرجى المحاولة مرة أخرى");
      setSuccess(null);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  if (loading) {
    return <div className="text-center py-8">جاري التحميل...</div>;
  }

  if (!settings) {
    return <div className="text-center py-8 text-red-600">فشل تحميل الإعدادات</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
          إدارة المحتوى والإعدادات
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg p-4 mb-4 shadow-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-red-600 dark:text-red-400 font-semibold mb-1">
                خطأ
              </p>
              <p className="text-red-600 dark:text-red-400 text-sm whitespace-pre-wrap">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex-shrink-0"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-lg p-4 mb-4 shadow-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-green-600 dark:text-green-400 font-semibold mb-1">
                نجح
              </p>
              <p className="text-green-600 dark:text-green-400 text-sm">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 flex-shrink-0"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow mb-6">
        <div className="border-b border-slate-200 dark:border-slate-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab("social")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === "social"
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              روابط التواصل الاجتماعي
            </button>
            <button
              onClick={() => setActiveTab("homepage")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === "homepage"
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              محتوى الصفحة الرئيسية
            </button>
            <button
              onClick={() => setActiveTab("config")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === "config"
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              الإعدادات والتكوين
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Social Media Tab */}
          {activeTab === "social" && (
            <SocialMediaForm
              settings={settings}
              onSave={handleSave}
              saving={saving}
            />
          )}

          {/* Homepage Tab */}
          {activeTab === "homepage" && (
            <HomepageForm
              settings={settings}
              onSave={handleSave}
              onImageUpload={handleImageUpload}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              isDragging={isDragging}
              saving={saving}
            />
          )}

          {/* Config Tab */}
          {activeTab === "config" && (
            <ConfigForm
              settings={settings}
              onSave={handleSave}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Social Media Form Component
const SocialMediaForm: React.FC<{
  settings: CmsSettings;
  onSave: (data: Partial<UpdateCmsSettingsRequest>) => void;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    facebookUrl: settings.facebookUrl || "",
    instagramUrl: settings.instagramUrl || "",
    twitterUrl: settings.twitterUrl || "",
    youtubeUrl: settings.youtubeUrl || "",
    whatsappNumber: settings.whatsappNumber || "",
    email: settings.email || "",
    storeLocation: settings.storeLocation || "",
  });

  // Update form data when settings change
  useEffect(() => {
    setFormData({
      facebookUrl: settings.facebookUrl || "",
      instagramUrl: settings.instagramUrl || "",
      twitterUrl: settings.twitterUrl || "",
      youtubeUrl: settings.youtubeUrl || "",
      whatsappNumber: settings.whatsappNumber || "",
      email: settings.email || "",
      storeLocation: settings.storeLocation || "",
    });
  }, [settings]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      facebookUrl: formData.facebookUrl || null,
      instagramUrl: formData.instagramUrl || null,
      twitterUrl: formData.twitterUrl || null,
      youtubeUrl: formData.youtubeUrl || null,
      whatsappNumber: formData.whatsappNumber || null,
      email: formData.email || null,
      storeLocation: formData.storeLocation || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          رابط Facebook
        </label>
        <input
          type="url"
          value={formData.facebookUrl}
          onChange={(e) => setFormData({ ...formData, facebookUrl: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="https://facebook.com/yourpage"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          رابط Instagram
        </label>
        <input
          type="url"
          value={formData.instagramUrl}
          onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="https://instagram.com/yourpage"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          رابط Twitter
        </label>
        <input
          type="url"
          value={formData.twitterUrl}
          onChange={(e) => setFormData({ ...formData, twitterUrl: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="https://twitter.com/yourpage"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          رابط YouTube
        </label>
        <input
          type="url"
          value={formData.youtubeUrl}
          onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="https://youtube.com/yourchannel"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          رقم WhatsApp
        </label>
        <input
          type="text"
          value={formData.whatsappNumber}
          onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="+213555123456"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          البريد الإلكتروني
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="info@example.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          موقع المتجر (GPS أو عنوان)
        </label>
        <textarea
          value={formData.storeLocation}
          onChange={(e) => setFormData({ ...formData, storeLocation: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary px-3 py-2 min-h-[80px]"
          placeholder="مثال: 36.7538,3.0588 أو 'الجزائر العاصمة، حي باب الزوار'"
          rows={3}
        />
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          يمكنك إدخال إحداثيات GPS (خط العرض، خط الطول) أو عنوان نصي
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
        >
          {saving ? "جاري الحفظ..." : "حفظ"}
        </button>
      </div>
    </form>
  );
};

// Homepage Form Component
const HomepageForm: React.FC<{
  settings: CmsSettings;
  onSave: (data: Partial<UpdateCmsSettingsRequest>) => void;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  isDragging: boolean;
  saving: boolean;
}> = ({ settings, onSave, onImageUpload, onDragOver, onDragLeave, onDrop, isDragging, saving }) => {
  const [formData, setFormData] = useState({
    homePageTitle: settings.homePageTitle || "",
    homePageSubtitle: settings.homePageSubtitle || "",
    homePageDescription: settings.homePageDescription || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      homePageTitle: formData.homePageTitle || null,
      homePageSubtitle: formData.homePageSubtitle || null,
      homePageDescription: formData.homePageDescription || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          صورة الصفحة الرئيسية
        </label>
        
        {/* Current Image Preview */}
        {settings.homePageImage && (
          <div className="mb-4">
            <div className="relative inline-block">
              <img
                src={`data:image/jpeg;base64,${settings.homePageImage}`}
                alt="Home page"
                className="w-full max-w-md rounded-lg shadow-lg"
                style={{ maxHeight: "400px", objectFit: "contain" }}
              />
            </div>
          </div>
        )}

        {/* Image Upload Area - Drag and Drop */}
        <div className="mb-4">
          <label
            htmlFor="homepage-image-upload"
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-all group ${
              isDragging
                ? "border-primary bg-primary/10 dark:bg-primary/20 scale-[1.02]"
                : "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 hover:border-primary/50"
            }`}
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg
                className={`w-12 h-12 mb-4 transition-colors ${
                  isDragging
                    ? "text-primary"
                    : "text-slate-400 dark:text-slate-500 group-hover:text-primary"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-primary">انقر للرفع</span> أو اسحب الصورة هنا
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                PNG, JPG, JPEG فقط - حتى 10MB
              </p>
            </div>
            <input
              id="homepage-image-upload"
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={onImageUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          العنوان الرئيسي
        </label>
        <input
          type="text"
          value={formData.homePageTitle}
          onChange={(e) => setFormData({ ...formData, homePageTitle: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="عنوان الصفحة الرئيسية"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          العنوان الفرعي
        </label>
        <input
          type="text"
          value={formData.homePageSubtitle}
          onChange={(e) => setFormData({ ...formData, homePageSubtitle: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="عنوان فرعي"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          الوصف
        </label>
        <textarea
          value={formData.homePageDescription}
          onChange={(e) => setFormData({ ...formData, homePageDescription: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-24 px-3 py-2"
          rows={4}
          placeholder="وصف الصفحة الرئيسية"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
        >
          {saving ? "جاري الحفظ..." : "حفظ"}
        </button>
      </div>
    </form>
  );
};

// Config Form Component
const ConfigForm: React.FC<{
  settings: CmsSettings;
  onSave: (data: Partial<UpdateCmsSettingsRequest>) => void;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    facebookPixelId: settings.facebookPixelId || "",
    googleAnalyticsId: settings.googleAnalyticsId || "",
    siteName: settings.siteName || "",
    siteDescription: settings.siteDescription || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      facebookPixelId: formData.facebookPixelId || null,
      googleAnalyticsId: formData.googleAnalyticsId || null,
      siteName: formData.siteName || null,
      siteDescription: formData.siteDescription || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Facebook Pixel ID
        </label>
        <input
          type="text"
          value={formData.facebookPixelId}
          onChange={(e) => setFormData({ ...formData, facebookPixelId: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="123456789012345"
        />
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          يتم استخدام هذا المعرف لتتبع تحويلات المستخدمين عبر Facebook Conversion API
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Google Analytics ID
        </label>
        <input
          type="text"
          value={formData.googleAnalyticsId}
          onChange={(e) => setFormData({ ...formData, googleAnalyticsId: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="G-XXXXXXXXXX"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          اسم الموقع
        </label>
        <input
          type="text"
          value={formData.siteName}
          onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-10 px-3"
          placeholder="اسم المتجر"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          وصف الموقع
        </label>
        <textarea
          value={formData.siteDescription}
          onChange={(e) => setFormData({ ...formData, siteDescription: e.target.value })}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary h-24 px-3 py-2"
          rows={4}
          placeholder="وصف الموقع"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
        >
          {saving ? "جاري الحفظ..." : "حفظ"}
        </button>
      </div>
    </form>
  );
};

export default Cms;

